import React, { useCallback, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  ClipboardCheck,
  Clock,
  Layers,
  Moon,
  Package,
  PenLine,
  Search,
  Sun,
  Unlock,
} from "lucide-react";
import { optionsService } from "../../services/optionsService";
import {
  desenvolvedorService,
  type FichaDiariaDevAssinaturaTipo,
  type FichaDiariaDevDetalhe,
} from "../../services/desenvolvedorService";
import { COLORS } from "../../constants";
import { BrazilDate } from "../../utils/BrazilDate";
import { ControleProducaoFichaDiariaPage } from "../ControleProducao/FichaDiaria/index";

type ConfirmAssinaturaState = {
  acao: "desmarcar" | "liberar" | "forcar";
  tipo: FichaDiariaDevAssinaturaTipo;
  setorId?: number;
  titulo: string;
  texto: string;
};

type ConfirmState =
  | ConfirmAssinaturaState
  | {
      acao: "liberar-edicao";
      titulo: string;
      texto: string;
    };

function formatDiaBr(iso: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return `${String(d).padStart(2, "0")}/${String(m).padStart(2, "0")}/${y}`;
}

function formatarDataAssinatura(iso?: string | null): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function cloneDetalhe(d: FichaDiariaDevDetalhe): FichaDiariaDevDetalhe {
  return JSON.parse(JSON.stringify(d)) as FichaDiariaDevDetalhe;
}

const FichaDiariaDev: React.FC = () => {
  const [lojaId, setLojaId] = useState<number | "">("");
  const [dataFicha, setDataFicha] = useState(() => BrazilDate.todayISO());
  const [detalhe, setDetalhe] = useState<FichaDiariaDevDetalhe | null>(null);
  const [fichaEmbedKey, setFichaEmbedKey] = useState(0);
  const [turnoVisao, setTurnoVisao] = useState<"dia" | "noite">("dia");
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const lojasQuery = useQuery({
    queryKey: ["lojas"],
    queryFn: () => optionsService.getLojas(),
  });

  const recentesQuery = useQuery({
    queryKey: ["desenvolvedor", "ficha-diaria-recentes", lojaId],
    queryFn: () =>
      desenvolvedorService.listarFichasDiariasRecentes({
        lojaId: lojaId as number,
      }),
    enabled: lojaId !== "",
  });

  const aplicarDetalhe = useCallback((data: FichaDiariaDevDetalhe) => {
    setDetalhe(cloneDetalhe(data));
    setFichaEmbedKey((k) => k + 1);
  }, []);

  const carregar = useCallback(
    async (loja: number, data: string) => {
      const result = await desenvolvedorService.obterFichaDiaria({
        lojaId: loja,
        data,
      });
      aplicarDetalhe(result);
      if (!result.ficha) {
        toast.info("Nenhuma ficha persistida nesta data.");
      }
    },
    [aplicarDetalhe],
  );

  const buscar = useCallback(async () => {
    if (lojaId === "") {
      toast.error("Selecione a loja.");
      return;
    }
    try {
      await carregar(lojaId, dataFicha);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao carregar ficha.");
    }
  }, [lojaId, dataFicha, carregar]);

  const desmarcarMutation = useMutation({
    mutationFn: (state: ConfirmAssinaturaState) => {
      if (lojaId === "") throw new Error("Selecione a loja.");
      return desenvolvedorService.desmarcarAssinaturasFichaDiaria({
        lojaId,
        data: dataFicha,
        tipo: state.tipo,
        setorId: state.setorId,
        confirmacao: true,
      });
    },
    onSuccess: (result) => {
      toast.success(result.message);
      setConfirm(null);
      aplicarDetalhe(result.detalhe);
      void recentesQuery.refetch();
    },
    onError: (e: Error) => {
      toast.error(e.message || "Falha ao desmarcar assinatura.");
    },
  });

  const liberarMutation = useMutation({
    mutationFn: (state: ConfirmAssinaturaState) => {
      if (lojaId === "") throw new Error("Selecione a loja.");
      return desenvolvedorService.liberarAssinaturasFichaDiaria({
        lojaId,
        data: dataFicha,
        tipo: state.tipo,
        setorId: state.setorId,
        confirmacao: true,
      });
    },
    onSuccess: (result) => {
      toast.success(result.message);
      setConfirm(null);
      aplicarDetalhe(result.detalhe);
      void recentesQuery.refetch();
    },
    onError: (e: Error) => {
      toast.error(e.message || "Falha ao liberar assinatura.");
    },
  });

  const liberarEdicaoMutation = useMutation({
    mutationFn: () => {
      if (lojaId === "") throw new Error("Selecione a loja.");
      return desenvolvedorService.liberarEdicaoFichaDiaria({
        lojaId,
        duracaoHoras: 3,
        confirmacao: true,
      });
    },
    onSuccess: (result) => {
      toast.success(result.message);
      setConfirm(null);
      aplicarDetalhe(result.detalhe);
      void recentesQuery.refetch();
    },
    onError: (e: Error) => {
      toast.error(e.message || "Falha ao liberar edição.");
    },
  });

  const forcarMutation = useMutation({
    mutationFn: (state: ConfirmAssinaturaState) => {
      if (lojaId === "") throw new Error("Selecione a loja.");
      return desenvolvedorService.forcarAssinaturasFichaDiaria({
        lojaId,
        data: dataFicha,
        tipo: state.tipo,
        setorId: state.setorId,
        confirmacao: true,
      });
    },
    onSuccess: (result) => {
      toast.success(result.message);
      setConfirm(null);
      aplicarDetalhe(result.detalhe);
      void recentesQuery.refetch();
    },
    onError: (e: Error) => {
      toast.error(e.message || "Falha ao assinar.");
    },
  });

  const pedirDesmarcar = useCallback(
    (state: Omit<ConfirmAssinaturaState, "acao">) => {
      setConfirm({ ...state, acao: "desmarcar" });
    },
    [],
  );

  const pedirLiberar = useCallback(
    (state: Omit<ConfirmAssinaturaState, "acao">) => {
      setConfirm({ ...state, acao: "liberar" });
    },
    [],
  );

  const pedirForcarAssinar = useCallback(
    (state: Omit<ConfirmAssinaturaState, "acao">) => {
      setConfirm({ ...state, acao: "forcar" });
    },
    [],
  );

  const ficha = detalhe?.ficha ?? null;
  const setoresDev = detalhe?.assinaturasSetores ?? [];
  const temSetoresDev = setoresDev.length > 0;
  const todosSetoresAssinados =
    temSetoresDev && setoresDev.every((s) => s.assinado);
  const algumSetorLiberado = setoresDev.some(
    (s) => s.liberadoDev && !s.assinado,
  );
  const algumaAssinada =
    ficha?.assinadoConfere === true ||
    ficha?.assinadoProducao === true ||
    setoresDev.some((s) => s.assinado);

  return (
    <div className="space-y-6">
      <section className="rounded-lg border border-red-300 bg-red-50 p-4 text-sm text-red-950">
        <p className="font-semibold">Só perfil Desenvolvedor</p>
        <p className="mt-1">
          O painel de assinaturas acima continua com todas as ferramentas (liberar
          edição 3h, liberar/desmarcar/assinar assinaturas). A ficha abaixo é a
          mesma da produção — escolha o turno <strong>DIA</strong> ou{" "}
          <strong>NOITE</strong> para ver e editar o contexto correto. Como
          desenvolvedor, você pode salvar e ajustar mesmo com ficha assinada.
        </p>
      </section>

      <section className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm sm:p-6">
        <h2 className="mb-3 flex items-center gap-2 text-lg font-bold text-neutral-900">
          <Search className="h-5 w-5" aria-hidden />
          Buscar ficha
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <label className="text-sm font-medium text-neutral-700">
            Loja
            <select
              className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              value={lojaId}
              onChange={(e) => {
                const v = e.target.value;
                setLojaId(v === "" ? "" : Number(v));
                setDetalhe(null);
                setFichaEmbedKey((k) => k + 1);
              }}
            >
              <option value="">Selecione</option>
              {(
                lojasQuery.data as { id: number; nome: string }[] | undefined
              )?.map((loja) => (
                <option key={loja.id} value={loja.id}>
                  {loja.nome}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm font-medium text-neutral-700">
            Data
            <input
              type="date"
              className="mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 text-sm"
              value={dataFicha}
              onChange={(e) => setDataFicha(e.target.value)}
            />
          </label>
          <div className="flex items-end">
            <button
              type="button"
              onClick={() => void buscar()}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-white"
              style={{ backgroundColor: COLORS.PRIMARY }}
            >
              Carregar ficha
            </button>
          </div>
        </div>

        {recentesQuery.data && recentesQuery.data.fichas.length > 0 ? (
          <div className="mt-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-neutral-500">
              Fichas recentes com assinatura
            </p>
            <div className="flex flex-wrap gap-2">
              {recentesQuery.data.fichas.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => {
                    setDataFicha(f.dataFicha);
                    if (lojaId === "") return;
                    void carregar(lojaId, f.dataFicha).catch((e) =>
                      toast.error(
                        e instanceof Error ? e.message : "Falha ao carregar.",
                      ),
                    );
                  }}
                  className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                    dataFicha === f.dataFicha
                      ? "border-red-300 bg-red-50 text-red-900"
                      : "border-neutral-200 bg-neutral-50 text-neutral-700 hover:bg-neutral-100"
                  }`}
                >
                  {formatDiaBr(f.dataFicha)}
                  {f.assinadoConfere ? " · conf." : ""}
                  {f.setoresAssinados > 0
                    ? ` · ${f.setoresAssinados}/${f.setoresTotal} set.`
                    : ""}
                  {f.assinadoProducao ? " · prod. total" : ""}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </section>

      {detalhe ? (
        <>
          <section className="rounded-lg border border-emerald-300 bg-emerald-50 p-4 shadow-sm sm:p-6">
            <h2 className="mb-2 flex items-center gap-2 text-lg font-bold text-emerald-950">
              <Clock className="h-5 w-5" aria-hidden />
              Edição temporária na ficha operacional
            </h2>
            <p className="mb-3 text-sm text-emerald-900">
              Libera por <strong>3 horas</strong> a edição completa (sobras,
              produção e observações) na Ficha de Produção para a equipe da loja
              — em <strong>qualquer data</strong> (30/08, 31/08, etc.).
            </p>
            {detalhe.edicaoLiberadaLojaAtiva && detalhe.edicaoLiberadaLojaAte ? (
              <p className="mb-3 text-sm font-medium text-emerald-800">
                {new Date(detalhe.edicaoLiberadaLojaAte) > new Date()
                  ? `Ativa para toda a loja até ${formatarDataAssinatura(detalhe.edicaoLiberadaLojaAte)}`
                  : "Não há liberação ativa no momento."}
                {detalhe.edicaoLiberadaLojaPorNome
                  ? ` · por ${detalhe.edicaoLiberadaLojaPorNome}`
                  : ""}
              </p>
            ) : ficha?.edicaoLiberadaAte ? (
              <p className="mb-3 text-sm font-medium text-emerald-800">
                {new Date(ficha.edicaoLiberadaAte) > new Date()
                  ? `Ativa só nesta data até ${formatarDataAssinatura(ficha.edicaoLiberadaAte)}`
                  : "Não há liberação ativa no momento."}
                {ficha.edicaoLiberadaPorNome
                  ? ` · por ${ficha.edicaoLiberadaPorNome}`
                  : ""}
              </p>
            ) : null}
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
              disabled={lojaId === "" || liberarEdicaoMutation.isPending}
              onClick={() =>
                setConfirm({
                  acao: "liberar-edicao",
                  titulo: "Liberar edição por 3 horas",
                  texto: `A equipe de ${detalhe.lojaNome} poderá editar sobras, produção e observações em qualquer data da ficha diária por 3 horas. Continuar?`,
                })
              }
            >
              <Clock className="h-4 w-4" aria-hidden />
              Liberar edição (3h) — toda a loja
            </button>
          </section>

          <section className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm sm:p-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-lg font-bold text-neutral-900">
                Assinaturas — {detalhe.lojaNome} · {formatDiaBr(detalhe.dataFicha)}
              </h2>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-semibold text-white"
                  onClick={() =>
                    pedirLiberar({
                      tipo: "todas",
                      titulo: "Liberar todas as assinaturas",
                      texto:
                        "Isso vai liberar conferência, produção de todos os setores e produção total para ajustarem os campos e assinarem — sem registrar sua assinatura. Continuar?",
                    })
                  }
                >
                  <Unlock className="h-4 w-4" aria-hidden />
                  Liberar todas
                </button>
                <button
                  type="button"
                  className="inline-flex items-center gap-1 rounded-lg bg-violet-700 px-3 py-1.5 text-sm font-semibold text-white"
                  onClick={() =>
                    pedirForcarAssinar({
                      tipo: "todas",
                      titulo: "Assinar todas em meu nome",
                      texto:
                        "Isso vai registrar conferência, produção de todos os setores e produção total com a sua assinatura, ignorando requisitos incompletos. Continuar?",
                    })
                  }
                >
                  <PenLine className="h-4 w-4" aria-hidden />
                  Assinar todas (eu)
                </button>
                {ficha && algumaAssinada ? (
                  <button
                    type="button"
                    className="rounded-lg bg-red-700 px-3 py-1.5 text-sm font-semibold text-white"
                    onClick={() =>
                      pedirDesmarcar({
                        tipo: "todas",
                        titulo: "Desmarcar todas as assinaturas",
                        texto:
                          "Isso vai desmarcar conferência, produção de todos os setores e produção total. Continuar?",
                      })
                    }
                  >
                    Desmarcar todas
                  </button>
                ) : null}
              </div>
            </div>

            {!ficha ? (
              <p className="mb-3 text-sm text-neutral-600">
                Não há ficha persistida nesta data. Liberar cria a ficha e
                desbloqueia as assinaturas, sem assinar em seu nome.
              </p>
            ) : null}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              <AssinaturaCard
                titulo="Conferência"
                Icon={ClipboardCheck}
                assinado={ficha?.assinadoConfere === true}
                liberado={ficha?.liberadoDevConferencia === true}
                nome={ficha?.userConfereNome}
                data={ficha?.dataAssinaturaConf}
                extra={
                  ficha?.ajustadoAdminConferencia
                    ? "Já teve ajuste administrativo"
                    : null
                }
                motivos={detalhe.bloqueiosConferencia}
                onDesmarcar={() =>
                  pedirDesmarcar({
                    tipo: "conferencia",
                    titulo: "Desmarcar conferência",
                    texto:
                      "A conferência será desmarcada. Se houver produção de setor ou produção total assinada, elas também serão desmarcadas. Continuar?",
                  })
                }
                onLiberar={() =>
                  pedirLiberar({
                    tipo: "conferencia",
                    titulo: "Liberar conferência",
                    texto:
                      "O conferente poderá ajustar sobras e assinar, mesmo com requisitos incompletos. Sua assinatura não será registrada. Continuar?",
                  })
                }
                onForcarAssinar={() =>
                  pedirForcarAssinar({
                    tipo: "conferencia",
                    titulo: "Assinar conferência em meu nome",
                    texto:
                      "A conferência será registrada com sua assinatura, mesmo com requisitos incompletos. Continuar?",
                  })
                }
              />
              <AssinaturaCard
                titulo={temSetoresDev ? "Produção (setores)" : "Produção"}
                Icon={Package}
                assinado={
                  temSetoresDev
                    ? todosSetoresAssinados
                    : ficha?.assinadoProducao === true
                }
                liberado={
                  temSetoresDev
                    ? algumSetorLiberado
                    : ficha?.liberadoDevProducao === true
                }
                nome={
                  temSetoresDev
                    ? todosSetoresAssinados
                      ? "Todos os setores"
                      : null
                    : ficha?.userProducaoNome
                }
                data={temSetoresDev ? null : ficha?.dataAssinaturaProducao}
                extra={
                  temSetoresDev
                    ? `${setoresDev.filter((s) => s.assinado).length}/${setoresDev.length} setores assinados`
                    : ficha?.ajustadoAdminProducao
                      ? "Já teve ajuste administrativo"
                      : null
                }
                motivos={
                  temSetoresDev
                    ? setoresDev
                        .filter((s) => !s.assinado && !s.liberadoDev)
                        .flatMap((s) =>
                          (s.motivos ?? []).map((m) => `${s.setorNome}: ${m}`),
                        )
                    : undefined
                }
                onDesmarcar={() =>
                  pedirDesmarcar({
                    tipo: "producao",
                    titulo: temSetoresDev
                      ? "Desmarcar produção dos setores"
                      : "Desmarcar produção",
                    texto: temSetoresDev
                      ? "Todas as assinaturas de produção por setor serão desmarcadas. A produção total permanece, se já estiver assinada. Continuar?"
                      : "A assinatura de produção será desmarcada. Continuar?",
                  })
                }
                onLiberar={() =>
                  pedirLiberar({
                    tipo: "producao",
                    titulo: temSetoresDev
                      ? "Liberar produção dos setores"
                      : "Liberar produção",
                    texto: temSetoresDev
                      ? "Os responsáveis poderão ajustar produção e assinar cada setor, mesmo sem cumprir todos os requisitos. Continuar?"
                      : "O responsável poderá ajustar produção e assinar, mesmo sem cumprir todos os requisitos. Continuar?",
                  })
                }
                onForcarAssinar={() =>
                  pedirForcarAssinar({
                    tipo: "producao",
                    titulo: temSetoresDev
                      ? "Assinar setores em meu nome"
                      : "Assinar produção em meu nome",
                    texto: temSetoresDev
                      ? "Todos os setores serão assinados com sua assinatura, ignorando requisitos incompletos. A produção total não é assinada aqui. Continuar?"
                      : "A produção será assinada com sua assinatura, ignorando requisitos incompletos. Continuar?",
                  })
                }
              />
              {temSetoresDev ? (
                <AssinaturaCard
                  titulo="Produção total dos setores"
                  Icon={Layers}
                  assinado={ficha?.assinadoProducao === true}
                  liberado={ficha?.liberadoDevProducao === true}
                  nome={ficha?.userProducaoNome}
                  data={ficha?.dataAssinaturaProducao}
                  extra={
                    ficha?.ajustadoAdminProducao
                      ? "Já teve ajuste administrativo"
                      : "Supervisor de produção. Pode assinar mesmo com pendências."
                  }
                  motivos={detalhe.bloqueiosProducaoTotal}
                  onDesmarcar={() =>
                    pedirDesmarcar({
                      tipo: "producao_total",
                      titulo: "Desmarcar produção total",
                      texto:
                        "A assinatura de produção total dos setores será desmarcada. As assinaturas por setor permanecem. Continuar?",
                    })
                  }
                  onLiberar={() =>
                    pedirLiberar({
                      tipo: "producao_total",
                      titulo: "Liberar produção total",
                      texto:
                        "O supervisor de produção poderá assinar a produção total, mesmo com conferência ou setores pendentes. Sua assinatura não será registrada. Continuar?",
                    })
                  }
                  onForcarAssinar={() =>
                    pedirForcarAssinar({
                      tipo: "producao_total",
                      titulo: "Assinar produção setores em meu nome",
                      texto:
                        "A produção total dos setores será registrada com sua assinatura (como supervisor de produção), mesmo com pendências. Continuar?",
                    })
                  }
                />
              ) : null}
            </div>

            {temSetoresDev ? (
            <div className="mt-4 space-y-2">
                <h3 className="text-sm font-semibold text-neutral-800">
                  Produção por setor
                </h3>
                <ul className="divide-y divide-neutral-100 rounded-md border border-neutral-200 text-sm">
                  {(detalhe.assinaturasSetores ?? []).map((setor) => (
                    <li
                      key={setor.setorId}
                      className="flex flex-wrap items-start justify-between gap-2 px-3 py-2"
                    >
                      <div className="min-w-0 flex-1">
                        <span className="font-medium">{setor.setorNome}</span>
                        {setor.assinado ? (
                          <span className="ml-2 text-neutral-500">
                            {setor.userNome ?? "assinado"}
                            {setor.dataAssinatura
                              ? ` · ${formatarDataAssinatura(setor.dataAssinatura)}`
                              : ""}
                          </span>
                        ) : setor.liberadoDev ? (
                          <span className="ml-2 text-emerald-700">
                            liberado — aguardando assinatura
                          </span>
                        ) : (
                          <span className="ml-2 text-neutral-400">
                            não assinado
                          </span>
                        )}
                        {!setor.assinado &&
                        !setor.liberadoDev &&
                        (setor.motivos ?? []).length > 0 ? (
                          <ul className="mt-1 list-disc pl-4 text-xs text-amber-800">
                            {(setor.motivos ?? []).slice(0, 4).map((m) => (
                              <li key={m}>{m}</li>
                            ))}
                            {(setor.motivos ?? []).length > 4 ? (
                              <li>
                                +{(setor.motivos ?? []).length - 4} outros
                              </li>
                            ) : null}
                          </ul>
                        ) : null}
                      </div>
                      {setor.assinado ? (
                        <button
                          type="button"
                          className="rounded-md border border-red-200 px-2 py-1 text-xs font-semibold text-red-800 hover:bg-red-50"
                          onClick={() =>
                            pedirDesmarcar({
                              tipo: "producao",
                              setorId: setor.setorId,
                              titulo: `Desmarcar ${setor.setorNome}`,
                              texto: `A assinatura de produção de ${setor.setorNome} será desmarcada. Continuar?`,
                            })
                          }
                        >
                          Desmarcar
                        </button>
                      ) : setor.liberadoDev ? (
                        <div className="flex flex-wrap gap-1">
                          <button
                            type="button"
                            className="rounded-md border border-red-200 px-2 py-1 text-xs font-semibold text-red-800 hover:bg-red-50"
                            onClick={() =>
                              pedirDesmarcar({
                                tipo: "producao",
                                setorId: setor.setorId,
                                titulo: `Desmarcar liberação ${setor.setorNome}`,
                                texto: `A liberação de ${setor.setorNome} será removida. Continuar?`,
                              })
                            }
                          >
                            Desmarcar liberação
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded-md bg-violet-700 px-2 py-1 text-xs font-semibold text-white"
                            onClick={() =>
                              pedirForcarAssinar({
                                tipo: "producao",
                                setorId: setor.setorId,
                                titulo: `Assinar ${setor.setorNome} em meu nome`,
                                texto: `A produção de ${setor.setorNome} será assinada com sua assinatura. Continuar?`,
                              })
                            }
                          >
                            <PenLine className="h-3 w-3" aria-hidden />
                            Assinar (eu)
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-2 py-1 text-xs font-semibold text-white"
                            onClick={() =>
                              pedirLiberar({
                                tipo: "producao",
                                setorId: setor.setorId,
                                titulo: `Liberar ${setor.setorNome}`,
                                texto: `A produção de ${setor.setorNome} será liberada para ajuste e assinatura pelo responsável. Continuar?`,
                              })
                            }
                          >
                            <Unlock className="h-3 w-3" aria-hidden />
                            Liberar
                          </button>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded-md bg-violet-700 px-2 py-1 text-xs font-semibold text-white"
                            onClick={() =>
                              pedirForcarAssinar({
                                tipo: "producao",
                                setorId: setor.setorId,
                                titulo: `Assinar ${setor.setorNome} em meu nome`,
                                texto: `A produção de ${setor.setorNome} será assinada com sua assinatura, ignorando requisitos. Continuar?`,
                              })
                            }
                          >
                            <PenLine className="h-3 w-3" aria-hidden />
                            Assinar (eu)
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
            </div>
            ) : null}
          </section>

          {lojaId !== "" ? (
            <section className="space-y-3">
              <div className="rounded-lg border border-neutral-200 bg-white p-4 shadow-sm">
                <h2 className="text-lg font-bold text-neutral-900">
                  Ficha diária — turno
                </h2>
                <p className="mt-1 text-sm text-neutral-600">
                  Mesma tela da produção, separada por turno para ficar claro o
                  que cada equipe vê e edita.
                </p>
                <div
                  className="mt-4 flex flex-wrap gap-2"
                  role="tablist"
                  aria-label="Turno da ficha"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={turnoVisao === "dia"}
                    className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
                      turnoVisao === "dia"
                        ? "bg-amber-600 text-white shadow-sm"
                        : "border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"
                    }`}
                    onClick={() => setTurnoVisao("dia")}
                  >
                    <Sun className="h-4 w-4" aria-hidden />
                    DIA — Produção
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={turnoVisao === "noite"}
                    className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-colors ${
                      turnoVisao === "noite"
                        ? "bg-indigo-700 text-white shadow-sm"
                        : "border border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50"
                    }`}
                    onClick={() => setTurnoVisao("noite")}
                  >
                    <Moon className="h-4 w-4" aria-hidden />
                    NOITE — Conferência
                  </button>
                </div>
                <p className="mt-3 text-xs text-neutral-500">
                  {turnoVisao === "dia" ? (
                    <>
                      Lançamento de produção por setor, cronômetros, Produziu e
                      assinatura de produção.
                    </>
                  ) : (
                    <>
                      Ficha unificada: base, sobras, observações e assinatura de
                      conferência.
                    </>
                  )}
                </p>
              </div>
              <ControleProducaoFichaDiariaPage
                key={`${lojaId}-${dataFicha}-${fichaEmbedKey}-${turnoVisao}`}
                modoDesenvolvedor
                lojaIdControlada={lojaId}
                dataFichaControlada={dataFicha}
                ocultarSeletoresLojaData
                turnoSimuladoControlado={turnoVisao}
                ocultarSeletorTurnoTeste
              />
            </section>
          ) : null}
        </>
      ) : null}

      {confirm ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-ficha-dev-title"
        >
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h2
              id="confirm-ficha-dev-title"
              className="text-lg font-bold text-neutral-900"
            >
              {confirm.titulo}
            </h2>
            <p className="mt-2 text-sm text-neutral-700">{confirm.texto}</p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-medium"
                disabled={
                  desmarcarMutation.isPending ||
                  liberarMutation.isPending ||
                  liberarEdicaoMutation.isPending ||
                  forcarMutation.isPending
                }
                onClick={() => setConfirm(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 ${
                  confirm.acao === "liberar-edicao"
                    ? "bg-emerald-700"
                    : confirm.acao === "liberar"
                      ? "bg-amber-600"
                      : confirm.acao === "forcar"
                        ? "bg-violet-700"
                        : "bg-red-700"
                }`}
                disabled={
                  desmarcarMutation.isPending ||
                  liberarMutation.isPending ||
                  liberarEdicaoMutation.isPending ||
                  forcarMutation.isPending
                }
                onClick={() => {
                  if (confirm.acao === "liberar-edicao") {
                    liberarEdicaoMutation.mutate();
                  } else if (confirm.acao === "liberar") {
                    liberarMutation.mutate(confirm);
                  } else if (confirm.acao === "forcar") {
                    forcarMutation.mutate(confirm);
                  } else {
                    desmarcarMutation.mutate(confirm);
                  }
                }}
              >
                {desmarcarMutation.isPending ||
                liberarMutation.isPending ||
                liberarEdicaoMutation.isPending ||
                forcarMutation.isPending
                  ? "Aplicando…"
                  : confirm.acao === "liberar-edicao"
                    ? "Sim, liberar edição"
                    : confirm.acao === "liberar"
                      ? "Sim, liberar"
                      : confirm.acao === "forcar"
                        ? "Sim, assinar"
                        : "Sim, desmarcar"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

function AssinaturaCard({
  titulo,
  Icon,
  assinado,
  liberado = false,
  nome,
  data,
  extra,
  motivos,
  onDesmarcar,
  onLiberar,
  onForcarAssinar,
}: {
  titulo: string;
  Icon: typeof ClipboardCheck;
  assinado: boolean;
  liberado?: boolean;
  nome?: string | null;
  data?: string | null;
  extra?: string | null;
  motivos?: string[];
  onDesmarcar: () => void;
  onLiberar: () => void;
  onForcarAssinar: () => void;
}) {
  const travada = !assinado && !liberado && (motivos?.length ?? 0) > 0;
  return (
    <div
      className={`rounded-lg border p-3 ${
        assinado
          ? "border-emerald-200 bg-emerald-50/60"
          : liberado
            ? "border-sky-300 bg-sky-50/70"
            : travada
              ? "border-amber-300 bg-amber-50/70"
              : "border-neutral-200 bg-neutral-50"
      }`}
    >
      <div className="mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4" aria-hidden />
        <p className="text-sm font-semibold">{titulo}</p>
        {liberado && !assinado ? (
          <span className="rounded-full bg-sky-200 px-2 py-0.5 text-[10px] font-bold uppercase text-sky-900">
            Liberado
          </span>
        ) : null}
        {travada ? (
          <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[10px] font-bold uppercase text-amber-900">
            Travada
          </span>
        ) : null}
      </div>
      {assinado ? (
        <>
          <p className="text-xs text-neutral-600">
            {nome ?? "Assinado"}
            {data ? ` · ${formatarDataAssinatura(data)}` : ""}
          </p>
          {extra ? (
            <p className="mt-1 text-[11px] text-amber-800">{extra}</p>
          ) : null}
          <button
            type="button"
            className="mt-2 rounded-md bg-red-700 px-2 py-1 text-xs font-semibold text-white"
            onClick={onDesmarcar}
          >
            Desmarcar
          </button>
        </>
      ) : liberado ? (
        <>
          <p className="text-xs text-sky-900">
            Liberado para ajuste e assinatura pelo responsável.
          </p>
          <div className="mt-2 flex flex-wrap gap-1">
            <button
              type="button"
              className="rounded-md bg-red-700 px-2 py-1 text-xs font-semibold text-white"
              onClick={onDesmarcar}
            >
              Desmarcar liberação
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md bg-violet-700 px-2 py-1 text-xs font-semibold text-white"
              onClick={onForcarAssinar}
            >
              <PenLine className="h-3 w-3" aria-hidden />
              Assinar (eu)
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="text-xs text-neutral-500">Não assinada</p>
          {travada ? (
            <ul className="mt-2 list-disc pl-4 text-xs text-amber-900">
              {(motivos ?? []).slice(0, 4).map((m) => (
                <li key={m}>{m}</li>
              ))}
              {(motivos ?? []).length > 4 ? (
                <li>+{(motivos ?? []).length - 4} outros</li>
              ) : null}
            </ul>
          ) : null}
          <div className="mt-2 flex flex-wrap gap-1">
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md bg-amber-600 px-2 py-1 text-xs font-semibold text-white"
              onClick={onLiberar}
            >
              <Unlock className="h-3 w-3" aria-hidden />
              Liberar
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-md bg-violet-700 px-2 py-1 text-xs font-semibold text-white"
              onClick={onForcarAssinar}
            >
              <PenLine className="h-3 w-3" aria-hidden />
              Assinar (eu)
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default FichaDiariaDev;
